/**
 * The dials a network view exposes, and one set of defaults.
 *
 * Settings rather than default parameters on the component, matching `viewSettings.ts`: a default
 * spread across a call stack is a behavior nobody can predict from a call site, whereas one
 * object with one documented default makes every deviation from it visible in the diff.
 */

import { DEFAULT_CAMERA, toTrackballCamera } from '../geometry/projection';
import type { CameraState, TrackballCamera } from '../types';
import type { NetworkLayoutKind } from './layout/buildNetworkLayout';

export type Network3DViewSettings = {
  layoutKind: NetworkLayoutKind;
  /** Force-layout iterations. The tree layouts are closed-form and ignore it. */
  iterations: number;
  /** Holten's beta for the bundled layout. 1 hugs the hierarchy, 0 is a straight line. */
  bundleStrength: number;
  /** Multiplier on z after the unit-cube fit, for pulling a layered stack apart. */
  zStretch: number;
  /**
   * The radius of a typical mark, in world units.
   *
   * A size rather than a multiplier, because `toRelativeNodeSizes` normalises against the median:
   * the mark at the middle of the estate comes out exactly this, and everything else is a capped
   * ratio of it. That is what makes the dial mean the same thing whether size is standing for a
   * reference count or for an annual cost.
   *
   * Does not apply to the grouped layout, which takes a mark's size from the space it packed for
   * it. That is not an oversight: the packing's non-overlap guarantee only means anything if the
   * drawn radius is the reserved one.
   */
  nodeScale: number;
  /** Reference edge weight, in CSS pixels. */
  edgeWidth: number;
  /** Structure weight, in CSS pixels. */
  guideWidth: number;
  /** Reference edge opacity at rest. */
  edgeOpacity: number;
  /**
   * How thick a group's cloud is through its densest part, as an optical depth.
   *
   * The grouped layout only. Zero draws no regions at all, which leaves a grouping readable
   * purely as where the marks sit - worth having as the honest bottom of the dial rather than as
   * a separate switch.
   */
  groupCloudDensity: number;
  /**
   * Whether marks and regions are named on the canvas.
   *
   * One switch rather than a choice of what to name, because the reader's decision is whether
   * they want text at all - which of a few hundred marks can be named at a given camera is a
   * declutter, and `placeNetworkLabels` is better placed to answer it than a dropdown is.
   */
  showLabels: boolean;
};

export const DEFAULT_NETWORK_3D_VIEW_SETTINGS: Network3DViewSettings = {
  layoutKind: 'force',
  /* Enough for a few hundred nodes to settle. Past this the layout is still moving, but by less
     than a node radius per step, so more iterations cost time and change no reading. */
  iterations: 320,
  bundleStrength: 0.85,
  zStretch: 1,
  /* Chosen to leave the picture where the old absolute scale had it: a typical mark there was a
     weight of about three drawn at 0.014 per square root, which is this. */
  nodeScale: 0.025,
  /* Thin, per the mark spec. An edge is context for the nodes it joins, and at 2px a few thousand
     of them are the picture instead. */
  edgeWidth: 1.1,
  guideWidth: 1,
  /* Low enough that crossing edges accumulate into visible density rather than a solid mass, which
     is what carries "many references here" without drawing each one darker. */
  edgeOpacity: 0.42,
  /* Measured against the drawn result rather than reasoned from the alpha, because the target is
     an `-srgb` view: a tenth of a hue added in linear encodes to a little under a third on screen,
     and the first value picked from the arithmetic came out as opaque balloons. A tenth is where a
     region reads as a place the marks are inside rather than as an object in front of them. */
  groupCloudDensity: 0.09,
  /* On. A diagram whose marks are anonymous until hovered is a picture of a graph rather than a
     graph, and the declutter is what makes that affordable at a few hundred marks. */
  showLabels: true,
};

/**
 * The orbit camera the network views open at.
 *
 * `heightScale` is 1 and must stay 1: the matrix applies it to the z axis, and the node billboards
 * are expanded along the camera axes in world space, so any other value stretches every sphere into
 * an ellipse. `zStretch` on the layout is the dial that does what `heightScale` does for a surface.
 *
 * More perspective than the surface default. A surface keeps it weak so heights stay comparable
 * across the plane; a graph has no such contract and needs the depth cue far more, since telling a
 * near node from a far one is most of reading it.
 */
export const NETWORK_TURNTABLE_CAMERA: CameraState = {
  yaw: DEFAULT_CAMERA.yaw,
  pitch: 0.34,
  zoom: 1,
  perspective: 0.34,
  heightScale: 1,
  /* The origin, which is where every camera here has always pivoted. */
  target: [0, 0, 0],
};

/** The same opening view on a trackball, which the network views orbit with. */
export const NETWORK_CAMERA: TrackballCamera = toTrackballCamera(
  NETWORK_TURNTABLE_CAMERA
);
